import {useState, useEffect, useMemo, useRef} from "react";
import {useApi} from "../hooks/useApi";
import {isCompleted, isPinnedOrientation} from "../utils/getProgressData";
import getWatchedProgress from "../utils/getWatchedProgress";
import {
  ALWAYS_SHOW_LANGUAGE_TOGGLE,
  DEFAULT_LANGUAGE,
  availableLanguages,
  filterByLanguage,
} from "../utils/videoLanguage";
import useMyCase from "../../myCase/useMyCase";
import {
  hiddenOrientationFamily,
  orientationFamilyOf,
  visibleCatalog,
} from "../../home/orientationRules";

export const useVideos = ({
  filteredQuery,
  watchHistory,
  language = DEFAULT_LANGUAGE,
  user,
}) => {
  const [videos, setVideos] = useState([]);
  // Starts true so the dashboard can show a clean full-screen loader
  // until the catalog arrives, instead of flashing its chrome over an
  // empty grid.
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(filteredQuery || "");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortValue, setSortValue] = useState("in-progress");

  const {fetchVideos} = useApi();

  useEffect(() => {
    fetchVideos()
      .then(setVideos)
      .finally(() => setIsLoading(false));
  }, [fetchVideos, setVideos]);

  useEffect(() => {
    setSearchQuery(filteredQuery || "");
  }, [filteredQuery, setSearchQuery]);

  // Round-3 #6: previous attempt re-sorted whenever a *new* id appeared
  // in watchHistory. That meant the moment a user pressed Play on a
  // "Not Started" video, the card would jump out of the visible filter
  // band and the user would see it "disappear" mid-watch. The actual
  // bug Khubaib reported was that this jump happens *as soon as
  // playback starts*, not on every tick.
  //
  // Fix: snapshot each video's sort bucket and freeze it for the
  // lifetime of the current sort/filter/search/video-list. Only an
  // explicit user action (changing the sort dropdown, the category
  // filter, the search box, or the video list itself updating from
  // the API) refreshes the snapshot. Live status badges on the cards
  // still update -- only the *position in the grid* is frozen.
  const statusSnapshotRef = useRef({});

  // Which languages the catalog actually contains. The dashboard renders the
  // toggle only when there is more than one, so an English-only environment
  // is visually unchanged by this feature.
  const languages = useMemo(() => availableLanguages(videos), [videos]);

  // The catalog scoped to the chosen language. Browse (sections/playlists) and
  // search both derive from this, so a learner never sees a Spanish card in an
  // English view. `videos` stays UNfiltered on purpose: it is what a ?v= deep
  // link resolves against, so a shared link to a Spanish video still opens
  // regardless of the reader's toggle.
  // Fall back when the stored preference has no content in this catalog.
  // Without this a learner who once chose Español and then loads a catalog
  // with only English videos gets an EMPTY dashboard and no way out: the
  // toggle hides itself when just one language is present, so there is no
  // control left to switch back with. Prefer the stored choice when it has
  // videos, else the first language that does.
  const effectiveLanguage = useMemo(() => {
    // The fallback exists only to prevent a dead end: an empty catalog with
    // the toggle hidden leaves no way back. When the toggle is always shown
    // there IS a way back, so honour the choice and let the empty state say
    // plainly that this language has no videos yet.
    if (ALWAYS_SHOW_LANGUAGE_TOGGLE) return language;
    if (languages.length === 0) return language; // still loading
    if (languages.some((l) => l.code === language)) return language;
    return languages[0].code;
  }, [languages, language]);

  // The participant's own programs, the one piece of context the dashboard
  // never had (hence the "no per-user program context" caveats in
  // getProgressData/taxonomy). Used ONLY to drop the orientation video of the
  // program this participant isn't in -- see orientationRules. A loading or
  // failed case view yields null and changes nothing.
  const {status: caseStatus, caseData} = useMyCase(user);
  const hiddenFamily =
    caseStatus === "success"
      ? hiddenOrientationFamily(caseData?.referrals)
      : null;
  // The catalog is not ready until the case view has ANSWERED. /my-case and
  // /get-videos race, and when the catalog wins the grid would render with
  // hiddenFamily still null -- both orientation videos on screen, openable,
  // until the case lands and one vanishes under the learner. Waiting costs a
  // beat on first load and is the whole point of the feature.
  //
  // "Answered" includes error: useMyCase resolves to "error" on a failed
  // fetch AND immediately when there is no customer_id, so this can never
  // hang. An errored case view leaves hiddenFamily null and the catalog
  // unfiltered, exactly as before.
  const caseSettled = caseStatus !== "loading";

  const inLanguage = useMemo(
    // Browse (sections/playlists) and search both derive from this, so the
    // other program's orientation video disappears from BOTH -- a video that
    // is hidden from a search for "Orientation" but sitting in the intro slot
    // one tab over would be the same bug with extra steps.
    () =>
      visibleCatalog(filterByLanguage(videos, effectiveLanguage), hiddenFamily),
    [videos, effectiveLanguage, hiddenFamily],
  );

  const getLiveStatus = (video, history) => {
    const watched = getWatchedProgress(history?.[video.id]);
    if (!(watched > 0)) return "not-started";
    if (isCompleted(watched, video)) return "completed";
    return "in-progress";
  };

  const filtered = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    const items = inLanguage.filter(
      (v) =>
        (v?.title || "").toLowerCase().includes(q) &&
        (selectedCategory === "All" || v?.category === selectedCategory),
    );

    // Rebuild the snapshot for the *current* filtered list using the
    // *current* watchHistory at the moment a sort/filter/search change
    // fires. The next watchHistory tick will not re-enter this branch
    // (watchHistory is intentionally NOT in deps), so card positions
    // stay stable until the user changes their view.
    const nextSnapshot = {};
    items.forEach((v) => {
      nextSnapshot[v.id] = getLiveStatus(v, watchHistory);
    });
    statusSnapshotRef.current = nextSnapshot;

    return [...items].sort((a, b) => {
      // Pin the orientation/intro video to the very top of the grid, ahead
      // of the status-bucket sort. It's the program's required first step,
      // so it should be the first thing a learner sees regardless of
      // progress. The default pin stays SNAP-only (isPinnedOrientation):
      // with no case view there's no program context, and pinning TANF would
      // put it atop every (mostly SNAP) learner's grid. But once the case
      // view says this is a TANF participant, the TANF video IS their
      // required first watch and the only orientation left in the list, so
      // it takes the slot the SNAP one just vacated.
      const pinned = (v) =>
        hiddenFamily === "SNAP"
          ? orientationFamilyOf(v) === "TANF"
          : isPinnedOrientation(v);
      const orientA = pinned(a);
      const orientB = pinned(b);
      if (orientA !== orientB) return orientA ? -1 : 1;

      const statusA =
        statusSnapshotRef.current[a.id] ?? getLiveStatus(a, watchHistory);
      const statusB =
        statusSnapshotRef.current[b.id] ?? getLiveStatus(b, watchHistory);
      if (statusA === sortValue && statusB !== sortValue) return -1;
      if (statusA !== sortValue && statusB === sortValue) return 1;
      return 0;
    });
    // `watchHistory` is read inside the closure but intentionally
    // omitted from deps -- see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inLanguage, searchQuery, selectedCategory, sortValue, hiddenFamily]);

  return {
    videos,
    inLanguage,
    languages,
    // The language actually in effect, which may differ from the requested
    // one (see effectiveLanguage). The toggle renders from this so its
    // highlighted option always matches the videos on screen.
    language: effectiveLanguage,
    filtered,
    isLoading: isLoading || !caseSettled,
    selectedCategory,
    sortValue,
    setSearchQuery,
    setSelectedCategory,
    setSortValue,
  };
};
